import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Flag, ClipboardList } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { STATUS, cap, festivos, isBD, iso, shortName } from './calendarLogic';
import { StatusBadge } from './StatusBadge';

const STEP_CHIP = { done: 'bg-blue-100 text-blue-800 border-blue-200', late: 'bg-red-100 text-red-800 border-red-200', now: 'bg-amber-100 text-amber-800 border-amber-200', next: 'bg-slate-100 text-slate-700 border-slate-200' };

const eventsFor = (k, PL, hb) => {
  const ev = [];
  PL.forEach(({ o, pl }) => {
    pl.rows.forEach((r) => { if (hb && iso(r.st) <= k && k <= iso(r.en)) ev.push({ o, pl, label: r.n, cls: STEP_CHIP[r.state] }); });
    (o.tareas || []).forEach((t) => { if (t.fecha === k) ev.push({ o, pl, label: t.desc, task: true, cls: t.hecha ? 'bg-violet-50 text-violet-400 border-violet-100 line-through' : 'bg-violet-100 text-violet-800 border-violet-200' }); });
    if (iso(pl.dead) === k) ev.push({ o, pl, label: 'Fecha límite', dead: true, cls: `${STATUS[pl.status].chip} font-bold` });
  });
  return ev;
};

const DayCell = ({ d, today, PL, onOpen }) => {
  const k = iso(d), hb = isBD(d), ev = eventsFor(k, PL, hb), fest = festivos(d.getFullYear()).has(k);
  return (
    <button data-testid={`cal-day-${k}`} onClick={() => ev.length && onOpen(k, ev)}
      className={`min-h-[112px] p-1.5 text-left flex flex-col gap-1 transition-colors ${hb ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/80'} ${k === today ? 'ring-2 ring-inset ring-indigo-600 bg-indigo-50/40' : ''}`}>
      <span className={`text-xs font-bold ${hb ? 'text-slate-800' : 'text-slate-400'}`}>{d.getDate()}{fest ? <span className="ml-1 text-[10px] font-medium text-slate-400">festivo</span> : null}</span>
      {ev.slice(0, 3).map((e, j) => (
        <span key={j} title={`${e.o.cliente} · ${e.label}`} className={`text-[10.5px] leading-tight px-1.5 py-0.5 rounded border truncate flex items-center gap-1 ${e.cls}`}>
          {e.dead ? <Flag size={10} className="shrink-0" /> : null}{e.task ? <ClipboardList size={10} className="shrink-0" /> : null}{shortName(e.o.cliente)} · {e.label}
        </span>
      ))}
      {ev.length > 3 ? <span className="text-[10px] text-slate-500 font-semibold">+{ev.length - 3} más</span> : null}
    </button>
  );
};

export const CalendarTab = ({ PL, today }) => {
  const [mes, setMes] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [day, setDay] = useState(null);
  const y = mes.getFullYear(), mo = mes.getMonth(), off = (mes.getDay() + 6) % 7, nd = new Date(y, mo + 1, 0).getDate();
  const cells = [...Array(off).fill(null), ...Array.from({ length: nd }, (_, i) => new Date(y, mo, i + 1))];
  const nav = 'p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 transition-colors';
  return (
    <div data-testid="tab-content-calendario">
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="cal-prev" className={nav} onClick={() => setMes(new Date(y, mo - 1, 1))}><ChevronLeft size={16} /></button>
        <h2 data-testid="cal-month-label" className="flex-1 text-center text-lg font-bold text-slate-900">{cap(mes.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' }))}</h2>
        <button data-testid="cal-today" className="px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-sm font-semibold transition-colors" onClick={() => { const d = new Date(); setMes(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Hoy</button>
        <button data-testid="cal-next" className={nav} onClick={() => setMes(new Date(y, mo + 1, 1))}><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((x) => <div key={x} className="bg-slate-100 py-2 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">{x}</div>)}
        {cells.map((d, i) => (d ? <DayCell key={iso(d)} d={d} today={today} PL={PL} onOpen={(k, ev) => setDay({ k, ev })} /> : <div key={`e${i}`} className="bg-slate-50" />))}
      </div>
      <div className="flex flex-wrap gap-3 mt-4 text-xs text-slate-600">
        {[['bg-red-400', 'Paso atrasado'], ['bg-amber-400', 'En curso'], ['bg-slate-300', 'Próximo'], ['bg-blue-400', 'Listo'], ['bg-violet-400', 'Tarea']].map(([c, l]) => <span key={l} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${c}`} />{l}</span>)}
        <span className="flex items-center gap-1.5"><Flag size={12} /> Fecha límite (color = estado del pedido)</span>
      </div>
      <Dialog open={!!day} onOpenChange={(v) => !v && setDay(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="cal-day-dialog">
          <DialogHeader><DialogTitle>{day ? cap(new Date(`${day.k}T00:00`).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })) : ''}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {day?.ev.map((e, j) => (
              <div key={j} className="flex items-center justify-between gap-2 p-3 rounded-lg border border-slate-200">
                <div><p className="font-semibold text-slate-900 text-sm">{e.o.cliente}</p><p className="text-xs text-slate-500">{e.dead ? 'Fecha límite de entrega' : e.task ? `Tarea: ${e.label}` : e.label} · {e.pl.type.n}</p></div>
                <StatusBadge pl={e.pl} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
